import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useStore } from './store/useStore';
import {
  getProjects, getProject, getDisciplines, getVersionSets, getSheets,
  getSheet,
  getUsers, getProjectTemplates,
  getCurrentUserId, setCurrentUserId,
  getAuthToken, setAuthToken, fetchAuthMe,
  getImpersonator, setImpersonator,
} from './utils/api';
import TopBar          from './components/TopBar';
import ProjectsPage    from './components/ProjectsPage';
import ProjectLayout   from './components/ProjectLayout';
import AdminConsole    from './components/AdminConsole';
import UploadModal     from './components/UploadModal';
import CreateProjectModal from './components/CreateProjectModal';
import CalibrationModal   from './components/CalibrationModal';
import SignInScreen    from './components/SignInScreen';
import AccountSetupPage from './components/AccountSetupPage';
import PublicShareViewer from './components/PublicShareViewer';
import styles from './App.module.css';

export default function App() {
  const {
    view,
    project,
    currentUser,
    signedIn,
    setDisciplines, setVersionSets, setSheets,
    setProjects,
    setUsers, setCurrentUser,
    setProjectTemplates,
    setView, setProject, setProjectPage, setDrawingsViewMode, setActiveSheet,
    activeSheet, drawingsViewMode,
    filterDiscipline, filterVersionSet, searchQuery,
    setRole,
    uploadModalOpen, createProjectModalOpen, calibrationModalOpen,
  } = useStore();

  // Deep-link spinner — held true on initial mount when the URL has a
  // `?sheet=<id>` param, flipped false in the deep-link effect's finally
  // block once the open succeeds or fails. Without this, React renders the
  // default <ProjectsPage /> for ~300ms while getSheet + getProject resolve,
  // which causes a visible flash of the projects grid before the viewer
  // appears. Reading from `window.location.search` synchronously inside
  // useState's lazy initializer guarantees the gate's up before first paint.
  const [deepLinkInFlight, setDeepLinkInFlight] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('sheet');
  });

  // ── Bootstrap: try to restore a session from a stored JWT ──
  // Runs once on mount. If a token survives (returning user), we
  // populate `currentUser` from /api/auth/me and flip signedIn so the
  // app skips the sign-in screen. If the token is invalid/expired,
  // axios's 401 handler clears it and reloads — landing on the sign-
  // in screen.
  useEffect(() => {
    // Bootstrap runs once on mount. The store initializer sets
    // `signedIn = !!cf.authToken` so a returning user doesn't flash
    // the sign-in screen, but `currentUser` starts null — fetch it
    // here. If the token is invalid, drop it and bounce to sign-in.
    if (!getAuthToken()) return;
    if (currentUser)   return; // already populated (HMR / re-mount)
    (async () => {
      try {
        const me = await fetchAuthMe();
        setCurrentUser(me);
        setCurrentUserId(me.id);
        useStore.getState().setSignedIn(true);
      } catch (_) {
        setAuthToken(null);
        setCurrentUserId(null);
        useStore.getState().setSignedIn(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // ── Boot: users + templates (after sign-in) ──
  // currentUser is populated by either the JWT-bootstrap effect above
  // or by SignInScreen on a fresh login. This effect just hydrates
  // the supporting reference data.
  useEffect(() => {
    if (!signedIn || !currentUser) return;
    (async () => {
      try {
        const users = await getUsers();
        setUsers(users);
        const templates = await getProjectTemplates();
        setProjectTemplates(templates);
      } catch (err) {
        console.error('Boot failed:', err);
      }
    })();
    // eslint-disable-next-line
  }, [signedIn, currentUser?.id]);

  // ── Load projects when current user changes ──
  useEffect(() => {
    if (!currentUser) return;
    getProjects().then(setProjects).catch(console.error);
    // eslint-disable-next-line
  }, [currentUser?.id]);

  // ── Deep-link: ?sheet=<id> opens that sheet in the viewer ───────────────
  // Used when a user clicks a section-cut hyperlink — that click fires
  // `window.open('?sheet=<id>', '_blank')`, and this effect in the new tab
  // resolves the id → project + sheet and jumps straight into the viewer.
  // Runs once per signed-in session; the URL is left intact so refresh
  // reopens the same sheet.
  useEffect(() => {
    if (!signedIn || !currentUser) return;
    const params = new URLSearchParams(window.location.search);
    const sheetId = params.get('sheet');
    if (!sheetId) {
      // No deep-link to resolve — make sure we're not stuck on the spinner
      // (defensive; the lazy init already returned false in this case).
      setDeepLinkInFlight(false);
      return;
    }
    (async () => {
      try {
        const sheet = await getSheet(sheetId);
        if (!sheet?.project_id) return;
        const proj = await getProject(sheet.project_id);
        if (!proj) return;
        // Jump into the viewer for this sheet. The per-project data
        // useEffects below will populate disciplines / version-sets /
        // sheets asynchronously; the viewer only needs `activeSheet`
        // to render the PDF + toolbar.
        setProject(proj);
        setView('project');
        setProjectPage('drawings');
        setDrawingsViewMode('viewer');
        setActiveSheet(sheet);
      } catch (err) {
        console.error('Deep-link open failed:', err);
      } finally {
        // Drop the spinner whether the open succeeded or failed — failure
        // means we land on the projects grid, which is the right fallback.
        setDeepLinkInFlight(false);
      }
    })();
    // eslint-disable-next-line
  }, [signedIn, currentUser?.id]);

  // ── Load per-project data when active project changes ──
  useEffect(() => {
    if (!project) return;
    getDisciplines(project.id).then(setDisciplines).catch(console.error);
    getVersionSets(project.id).then(setVersionSets).catch(console.error);
    // Per-project role: server returns the caller's effective role within
    // this project (admin / pm / super / field). When the project payload
    // already has it (the projects-page selector's per-project lookup
    // sets it), use that; otherwise refetch via GET /api/projects/:id.
    if (project.current_user_role) {
      setRole(project.current_user_role);
    } else {
      getProject(project.id)
        .then((p) => p?.current_user_role && setRole(p.current_user_role))
        .catch(console.error);
    }
    // eslint-disable-next-line
  }, [project?.id]);

  // When the user leaves a project (back to projects grid / admin console),
  // restore the org-wide role so the projects list / admin pages don't
  // inherit per-project gates.
  useEffect(() => {
    if (project) return;
    if (currentUser?.role) setRole(currentUser.role);
    // eslint-disable-next-line
  }, [project, currentUser?.role]);

  // (No version-set auto-default any more. The drawings list now defaults
  // to the deduped "latest version of each sheet number by issuance_date"
  // view — that's just `filterVersionSet === ''` plus the dedupe in
  // ProjectDrawings. Picking a specific VS in the dropdown opts out of
  // the dedupe and shows every sheet in that issuance.)

  // ── Browser tab title reflects the sheet currently being viewed ──
  // Makes it possible to tell tabs apart when several are open (common
  // when following section-cut links, which now open in new tabs).
  useEffect(() => {
    const DEFAULT = 'GumboGrid';
    if (drawingsViewMode === 'viewer' && activeSheet) {
      const num   = activeSheet.sheet_number || '';
      const title = activeSheet.title || '';
      const sheetStr = (title && title !== num) ? `${num} — ${title}` : num;
      document.title = sheetStr ? `${sheetStr} · ${DEFAULT}` : DEFAULT;
    } else {
      document.title = DEFAULT;
    }
  }, [drawingsViewMode, activeSheet?.id, activeSheet?.sheet_number, activeSheet?.title]);

  // ── Load sheets when project or filters change ──
  useEffect(() => {
    if (!project) return;
    const params = { project_id: project.id };
    if (filterDiscipline) params.discipline_id = filterDiscipline;
    if (filterVersionSet) params.version_set_id = filterVersionSet;
    if (searchQuery)      params.search         = searchQuery;
    getSheets(params).then(setSheets).catch(console.error);
    // eslint-disable-next-line
  }, [project?.id, filterDiscipline, filterVersionSet, searchQuery]);

  // Public share viewer — no sign-in required. URLs of the form
  // /share/<token> short-circuit the regular app shell and render a
  // standalone read-only PDF viewer. We do this BEFORE the sign-in gate
  // so external recipients can hit the link without an account.
  const shareMatch = typeof window !== 'undefined'
    && window.location.pathname.match(/^\/share\/([^\/?#]+)/);
  if (shareMatch) {
    return <PublicShareViewer token={shareMatch[1]} />;
  }

  if (!signedIn) {
    return <SignInScreen />;
  }

  // Signed in but hasn't finished first-time setup: force the
  // account-setup form (collects first/last/phone + creates a
  // password) BEFORE letting them into the app. The currentUser
  // gets refreshed inside AccountSetupPage on success and this
  // gate naturally falls through.
  if (currentUser && currentUser.password_set === false) {
    return <AccountSetupPage />;
  }

  // Deep-link gate — render a quiet "Opening sheet…" indicator instead of
  // the default <ProjectsPage /> while the deep-link effect resolves the
  // `?sheet=` param. Prevents the projects-grid flash users were seeing
  // when clicking a hyperlink markup in another tab.
  if (deepLinkInFlight) {
    return (
      <div className={styles.app}>
        <div className={styles.deepLinkLoading}>
          <span className={styles.deepLinkSpinner} aria-hidden />
          <span>Opening sheet…</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <ImpersonationBanner />
      <TopBar />
      <div className={styles.body}>
        {view === 'projects' && <ProjectsPage />}
        {view === 'project'  && <ProjectLayout />}
        {view === 'admin'    && <AdminConsole />}
      </div>
      {uploadModalOpen       && <UploadModal />}
      {createProjectModalOpen && <CreateProjectModal />}
      {calibrationModalOpen   && <CalibrationModal />}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border2)',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
            boxShadow: 'var(--shadow)',
          },
        }}
      />
    </div>
  );
}

// Sticky banner shown only while an admin is impersonating another
// user (kicked off from AdminUsersPanel's "Log in as" button). Reads
// the stash that AdminUsersPanel wrote on entry and offers a one-
// click "Return to <admin>" path that swaps the original JWT back in
// and reloads. Returns null when not impersonating.
function ImpersonationBanner() {
  const stash = getImpersonator();
  if (!stash?.originalToken) return null;
  const handleReturn = () => {
    setAuthToken(stash.originalToken);
    setCurrentUserId(stash.originalUserId || '');
    setImpersonator(null);
    // Hard reload — symmetric with the entry path; re-bootstrap from
    // the original admin JWT so every store field, role, and project
    // list reflects the admin's reality again.
    window.location.reload();
  };
  return (
    <div className={styles.impersonateBanner}>
      <span>
        Viewing as <strong>{stash.targetName}</strong>
      </span>
      <button className={styles.impersonateReturnBtn} onClick={handleReturn}>
        Return to {stash.originalName}
      </button>
    </div>
  );
}
